import { ArrowUp, ArrowsClockwise } from '@phosphor-icons/react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Empty } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/api/client';

export function LoadingState({ tip }: { tip?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Spinner size={32} className="text-primary" />
      {tip && (
        <span className="text-sm text-muted-foreground font-medium animate-pulse">
          {tip}
        </span>
      )}
    </div>
  );
}

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
}

export function ErrorState({ error, onRetry }: ErrorStateProps) {
  const apiError = error instanceof ApiError ? error : null;
  const message = apiError?.message ?? 'Something went wrong';
  const needsPlan = apiError?.isPaymentRequired ?? false;

  const title = needsPlan
    ? 'Your plan does not cover this'
    : apiError?.isAuthError
      ? 'Not authorised'
      : 'Could not load this';

  return (
    <Alert
      variant={needsPlan || apiError?.isAuthError ? 'warning' : 'error'}
      message={title}
      description={
        <div>
          <div>{message}</div>
          {apiError?.details?.length ? (
            <ul className="mt-2 pl-4 list-disc text-muted-foreground text-xs space-y-1">
              {apiError.details.slice(0, 5).map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          ) : null}
        </div>
      }
      action={
        needsPlan ? (
          <Link to="/app/billing">
            <Button size="sm" variant="default" className="gap-1 text-xs">
              <ArrowUp size={14} /> See plans
            </Button>
          </Link>
        ) : (
          onRetry && (
            <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={onRetry}>
              <ArrowsClockwise size={14} /> Retry
            </Button>
          )
        )
      }
      className="mb-4"
    />
  );
}

interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  image?: ReactNode;
}

export function EmptyState({
  title,
  description,
  action,
  image,
}: EmptyStateProps) {
  return (
    <Empty
      image={image}
      title={title}
      description={description}
      action={action}
      className="py-12"
    />
  );
}
